import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, message } from 'antd';
import AdminLogin from './components/AdminLogin';
import AdminLayout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserDetailsPage from './pages/UserDetails';
import Subscriptions from './pages/Subscriptions';
import TryOnFeedback from './pages/TryOnFeedback';
import TryOnFeedbackDetail from './pages/TryOnFeedbackDetail';
import Surveys from './pages/Surveys';
import Wallets from './pages/Wallets';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import Payments from './pages/Payments';
import InstagramBatchProcess from './pages/InstagramBatchProcess';
import Quests from './pages/Quests';
import Tours from './pages/Tours';
import Jobs from './pages/Jobs';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if admin key exists in localStorage
    const adminKey = localStorage.getItem('admin_key');
    setIsAuthenticated(!!adminKey);
    setIsLoading(false);
  }, []);

  const handleLogin = (adminKey: string) => {
    setIsAuthenticated(true);
    message.success('Successfully logged in to admin panel');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_key');
    setIsAuthenticated(false);
    message.info('Logged out successfully');
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1890ff',
            borderRadius: 6,
          },
        }}
      >
        <AntApp>
          <div className="App">
            {!isAuthenticated ? (
              <AdminLogin onLogin={handleLogin} />
            ) : (
              <Router>
                <AdminLayout onLogout={handleLogout}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/users/:userId" element={<UserDetailsPage />} />
                    <Route path="/subscriptions" element={<Subscriptions />} />
                    <Route path="/wallets" element={<Wallets />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/feedback" element={<TryOnFeedback />} />
                    <Route path="/feedback/:feedbackId" element={<TryOnFeedbackDetail />} />
                    <Route path="/surveys" element={<Surveys />} />
                    <Route path="/instagram-batch" element={<InstagramBatchProcess />} />
                    <Route path="/quests" element={<Quests />} />
                    <Route path="/tours" element={<Tours />} />
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/reports" element={
                      <div className="page-content" style={{ textAlign: 'center' }}>
                        <h2>Reports & Analytics</h2>
                        <p>This section will contain detailed reports and analytics dashboards.</p>
                      </div>
                    } />
                    <Route path="/settings" element={
                      <div className="page-content" style={{ textAlign: 'center' }}>
                        <h2>System Settings</h2>
                        <p>This section will contain system configuration and settings.</p>
                      </div>
                    } />
                    
                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </AdminLayout>
              </Router>
            )}
          </div>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
